import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Button,
  Typography,
  Modal,
  Spin,
  Tooltip,
  ConfigProvider,
} from 'antd';
import {
  CopyOutlined,
  UpOutlined,
  DownOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faArrowsRotate, faPlus, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { OptionThumbnail } from './OptionThumbnail';
import CustomizationSideBox from './CustomizationSideBox/CustomizationSideBox';
import CreateCustomizationDrawer from './CreateCustomizationDrawer/CreateCustomizationDrawer';
import {
  Container,
  HeaderCard,
  Title,
  Subtitle,
  ControlsRow,
  LastUpdatedText,
  RefreshButton,
  CreateButton,
  SearchCard,
  StyledSearchInput,
  ListContainer,
  CustomizationList,
  CustomizationCard,
  CustomizationHeader,
  CustomizationInfo,
  CustomizationTitle,
  CustomizationMeta,
  ActionButtons,
  IconButton,
  DuplicateButton,
  DuplicateIconButton,
  ActionIconButton,
  ExpandedContent,
  OptionItem,
  SoldOutButtonSmall,
  RelatedItemsSection,
  RelatedItemsTitle,
  RelatedItemText,
  PaginationText,
  FullWidthSpaceSmall,
  FlexContainerCenterPadding,
  StyledModal,
  DragDropListWrapper,
  DraggableItemWrapper,
  CustomizationInfoRow,
  SequenceText,
  OptionsScrollArea,
  OptionLeft,
  OptionTextColumn,
  OptionRight,
  OptionActionButtons,
  OptionName,
  OptionDescription,
  OptionPrice,
  TooltipContainer,
  TooltipHeader,
  TooltipInfoRow,
  TooltipLabel,
  TooltipValue,
  TooltipOptionsSection,
  TooltipOptionsTitle,
  TooltipOptionsList,
  TooltipOptionItem,
  TooltipSequenceBadge,
  TooltipOptionText,
} from './Styles';
import { CustomizationsProps } from './types';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useCustomizations } from './hooks/useCustomizations';

const Customizations: React.FC<CustomizationsProps> = ({ role, selectedCustomizationId, tabIndex }) => {
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );

  const {
    searchInput, isSearching, allCustomizations, lastUpdated, isFetching,
    expandedIndex, selectedItem, customizationData, showCreateForm,
    showDeleteDialog, customizationToDelete, isEditModalOpen, duplicatingId,
    isDeleting, customizationOrder, refreshing, editTargetOptionId,
    loading, total, hasMore,
    setSearchInput,
    handleRefresh,
    handleCreate, handleCloseCreateDrawer, handleCreateSubmit,
    handleItemClick, handleCloseEditModal,
    handleModalInputChange,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    handleDuplicateClick,
    toggleAccordion,
    submitSoldOut,
    handleEditOptionFromCard, handleDeleteOptionFromCard,
    handleDragEnd,
    handleSubmit,
    handleLoadMore,
  } = useCustomizations(selectedCustomizationId, tabIndex);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderCard>
            <Title level={1}>Customizations</Title>
            <Subtitle>Set menu schedules and availability</Subtitle>

            <ControlsRow>
              <LastUpdatedText>
                Last updated: {lastUpdated || 'Never'}
                <RefreshButton
                  icon={<FontAwesomeIcon spin={refreshing} icon={faArrowsRotate} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />}
                  onClick={handleRefresh}
                  size="small"
                />
              </LastUpdatedText>

              {role !== 'staff' && (
                <CreateButton onClick={handleCreate}>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
                  Create Customization
                </CreateButton>
              )}
            </ControlsRow>
          </HeaderCard>

          <SearchCard>
            <StyledSearchInput
              placeholder="Search customizations"
              prefix={isSearching ? null : <SearchOutlined />}
              suffix={isSearching ? <Spin size="small" /> : null}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
            />
          </SearchCard>

          <CreateCustomizationDrawer
            open={showCreateForm}
            onClose={handleCloseCreateDrawer}
            onSubmit={handleCreateSubmit}
          />

          <ListContainer>
            <CustomizationList>
              {loading && allCustomizations.length === 0 ? (
                <FlexContainerCenterPadding><Spin size="large" /></FlexContainerCenterPadding>
              ) : refreshing ? (
                <FlexContainerCenterPadding><Spin size="large" /></FlexContainerCenterPadding>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="customizations-list">
                    {(provided) => (
                      <DragDropListWrapper {...provided.droppableProps} ref={provided.innerRef}>
                        {customizationOrder.map((id, mapIndex) => {
                          const customization = allCustomizations.find((c) => c.id === id);
                          if (!customization) return null;
                          return (
                            <Draggable key={id} draggableId={String(id)} index={mapIndex}>
                              {(provided) => (
                                <DraggableItemWrapper
                                  $isReordering={false}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={provided.draggableProps.style}
                                >
                                  <CustomizationCard isSelected={selectedItem === customization.id} style={{ cursor: 'grab' }}>
                                    <CustomizationHeader>
                                      <CustomizationInfo>
                                        <CustomizationInfoRow>
                                          <FontAwesomeIcon
                                            icon={faGripVertical}
                                            fontSize={16}
                                            color={isDarkMode ? '#9ca3af' : '#6b7280'}
                                            style={{ cursor: 'grab', marginRight: '4px' }}
                                          />
                                          <Tooltip
                                            title={
                                              <TooltipContainer>
                                                <TooltipHeader>
                                                  <TooltipInfoRow>
                                                    <TooltipLabel>Order:</TooltipLabel>
                                                    <TooltipValue>{customization.sequence_number || 0}</TooltipValue>
                                                  </TooltipInfoRow>
                                                  <TooltipInfoRow>
                                                    <TooltipLabel>Options:</TooltipLabel>
                                                    <TooltipValue $color="#4fd1c7">{customization.option?.length || 0}</TooltipValue>
                                                  </TooltipInfoRow>
                                                  {customization.related_items && customization.related_items.length > 0 && (
                                                    <TooltipInfoRow>
                                                      <TooltipLabel>Related Items:</TooltipLabel>
                                                      <TooltipValue $color="#f6ad55">{customization.related_items.length}</TooltipValue>
                                                    </TooltipInfoRow>
                                                  )}
                                                </TooltipHeader>
                                                {customization.option && customization.option.length > 0 && (
                                                  <TooltipOptionsSection>
                                                    <TooltipOptionsTitle>Options Order:</TooltipOptionsTitle>
                                                    <TooltipOptionsList>
                                                      {[...(customization.option || [])]
                                                        .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                                                        .map((opt, idx) => (
                                                          <TooltipOptionItem key={idx}>
                                                            <TooltipSequenceBadge>#{opt.sequence_number || idx + 1}</TooltipSequenceBadge>
                                                            <TooltipOptionText>{opt.option}</TooltipOptionText>
                                                          </TooltipOptionItem>
                                                        ))}
                                                    </TooltipOptionsList>
                                                  </TooltipOptionsSection>
                                                )}
                                              </TooltipContainer>
                                            }
                                            overlayStyle={{ maxWidth: '380px' }}
                                            overlayInnerStyle={{ backgroundColor: '#2d3748', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                                          >
                                            <InfoCircleOutlined style={{ fontSize: '14px', color: isDarkMode ? '#9ca3af' : '#6b7280', cursor: 'help', marginRight: '4px' }} />
                                          </Tooltip>
                                          <CustomizationTitle level={5}>{customization.customization}</CustomizationTitle>
                                        </CustomizationInfoRow>
                                        <CustomizationMeta>
                                          {customization.option?.length || 0} options
                                          {customization.sequence_number !== undefined && (
                                            <SequenceText>
                                              • Order: {customization.sequence_number}
                                            </SequenceText>
                                          )}
                                        </CustomizationMeta>
                                      </CustomizationInfo>

                                      <ActionButtons>
                                        <Tooltip title="Edit customization">
                                          <ActionIconButton iconColor="#2D6CCA" onClick={(e) => { e.stopPropagation(); handleItemClick(customization); }}>
                                            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
                                          </ActionIconButton>
                                        </Tooltip>

                                        <Tooltip title="Duplicate customization">
                                          <DuplicateIconButton
                                            icon={duplicatingId === customization.id ? <Spin size="small" /> : <CopyOutlined />}
                                            loading={duplicatingId === customization.id}
                                            onClick={(e) => { e.stopPropagation(); handleDuplicateClick(customization); }}
                                          />
                                          <DuplicateButton
                                            icon={duplicatingId === customization.id ? <Spin size="small" /> : <CopyOutlined />}
                                            loading={duplicatingId === customization.id}
                                            onClick={(e) => { e.stopPropagation(); handleDuplicateClick(customization); }}
                                          >
                                            Duplicate
                                          </DuplicateButton>
                                        </Tooltip>

                                        <Tooltip title="Delete customization">
                                          <ActionIconButton iconColor="#f5222d" onClick={(e) => { e.stopPropagation(); handleDeleteClick(customization); }}>
                                            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
                                          </ActionIconButton>
                                        </Tooltip>

                                        <Tooltip title={expandedIndex === mapIndex ? 'Hide options' : 'Show options'}>
                                          <IconButton
                                            icon={expandedIndex === mapIndex ? <UpOutlined /> : <DownOutlined />}
                                            onClick={(e) => { e.stopPropagation(); toggleAccordion(mapIndex); }}
                                          />
                                        </Tooltip>
                                      </ActionButtons>
                                    </CustomizationHeader>

                                    {expandedIndex === mapIndex && (
                                      <ExpandedContent>
                                        <OptionsScrollArea>
                                          <FullWidthSpaceSmall vertical size="small">
                                            {[...(customization.option || [])]
                                              .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                                              .map((option, optionIndex) => (
                                                <OptionItem key={option.id ?? optionIndex}>
                                                  <OptionLeft>
                                                    <OptionThumbnail imageUrl={option.image || option.picture} altText={option.option} isDark={isDarkMode} />
                                                    <OptionTextColumn>
                                                      <OptionName strong>
                                                        {option.option}
                                                      </OptionName>
                                                      {option.description && (
                                                        <OptionDescription type="secondary">
                                                          {option.description}
                                                        </OptionDescription>
                                                      )}
                                                      <OptionPrice>
                                                        ${(option.price || 0).toFixed(2)}
                                                      </OptionPrice>
                                                    </OptionTextColumn>
                                                  </OptionLeft>
                                                  <OptionRight>
                                                    <SoldOutButtonSmall size="small" onClick={() => submitSoldOut(option, { ...option, sold_out: !option.sold_out })}>
                                                      {option.sold_out ? 'Set In Stock' : 'Set Sold Out'}
                                                    </SoldOutButtonSmall>
                                                    <OptionActionButtons>
                                                      <ActionIconButton iconColor="#2D6CCA" onClick={() => handleEditOptionFromCard(option, customization)}>
                                                        <FontAwesomeIcon icon={faPen} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                                                      </ActionIconButton>
                                                      <ActionIconButton iconColor="#f5222d" onClick={() => handleDeleteOptionFromCard(option)}>
                                                        <FontAwesomeIcon icon={faTrash} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                                                      </ActionIconButton>
                                                    </OptionActionButtons>
                                                  </OptionRight>
                                                </OptionItem>
                                              ))}
                                          </FullWidthSpaceSmall>
                                        </OptionsScrollArea>

                                        {customization.related_items && customization.related_items.length > 0 && (
                                          <RelatedItemsSection>
                                            <RelatedItemsTitle>Related Items ({customization.related_items.length}):</RelatedItemsTitle>
                                            {customization.related_items.map((relatedItem, itemIndex) => (
                                              <RelatedItemText key={itemIndex}>
                                                •{' '}
                                                {typeof relatedItem.item === 'object' && relatedItem.item?.item
                                                  ? relatedItem.item.item
                                                  : typeof relatedItem.item === 'string'
                                                  ? relatedItem.item
                                                  : relatedItem.name || 'Unknown Item'}
                                              </RelatedItemText>
                                            ))}
                                          </RelatedItemsSection>
                                        )}
                                      </ExpandedContent>
                                    )}
                                  </CustomizationCard>
                                </DraggableItemWrapper>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </DragDropListWrapper>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </CustomizationList>

            <StyledModal
              open={isEditModalOpen && selectedItem !== null && customizationData !== null}
              onCancel={handleCloseEditModal}
              footer={null}
              width={800}
              maskClosable={false}
              destroyOnClose
              style={{ top: 20 }}
            >
              {selectedItem && customizationData && (
                <CustomizationSideBox
                  customizationData={customizationData}
                  handleInputChange={handleModalInputChange}
                  handleDelete={() => {
                    const c = allCustomizations.find((c) => c.id === selectedItem);
                    if (c) { handleDeleteClick(c); handleCloseEditModal(); }
                  }}
                  handleDuplicate={async () => {
                    const c = allCustomizations.find((c) => c.id === selectedItem);
                    if (c) { await handleDuplicateClick(c); handleCloseEditModal(); }
                  }}
                  onClose={handleCloseEditModal}
                  isOpen={true}
                  handleSubmit={handleSubmit}
                  setCustomizationData={() => {}}
                  isNewCustomization={false}
                  selectedItem={selectedItem}
                  handleRefresh={handleRefresh}
                  width="100%"
                  isEditOption={false}
                  autoEditOptions={!!editTargetOptionId}
                  editTargetOptionId={editTargetOptionId}
                />
              )}
            </StyledModal>
          </ListContainer>

          {loading && allCustomizations.length > 0 && (
            <FlexContainerCenterPadding><Spin size="large" /></FlexContainerCenterPadding>
          )}

          {hasMore && allCustomizations.length > 0 && !loading && (
            <FlexContainerCenterPadding style={{ marginTop: 16 }}>
              <Button
                onClick={handleLoadMore}
                loading={isFetching}
                style={{ backgroundColor: '#2D6CCA', borderColor: '#2D6CCA', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1a4a8f'; e.currentTarget.style.borderColor = '#1a4a8f'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2D6CCA'; e.currentTarget.style.borderColor = '#2D6CCA'; }}
              >
                Load More (Showing {allCustomizations.length} of {total})
              </Button>
            </FlexContainerCenterPadding>
          )}

          {!hasMore && allCustomizations.length > 0 && (
            <FlexContainerCenterPadding style={{ marginTop: 16 }}>
              <PaginationText>Showing all {allCustomizations.length} of {total} customizations</PaginationText>
            </FlexContainerCenterPadding>
          )}

          <Modal
            open={showDeleteDialog}
            onCancel={handleDeleteCancel}
            onOk={handleDeleteConfirm}
            title="Delete Customization"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: isDeleting, icon: isDeleting ? <Spin size="small" /> : null }}
          >
            <Typography.Text>
              Are you sure you want to delete "{customizationToDelete?.customization}"? This action
              cannot be undone and will remove the customization from all associated items.
            </Typography.Text>
          </Modal>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Customizations);
